import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import {
  Box,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import React, { useEffect, useMemo, useState } from "react";
import { useStyles } from "./styles";
import { CustomTablePagination, EmailRow } from "./tableAction";
import { getComparator, tableSort } from "./tableUtils";

const CustomTable = ({
  columns = [],
  data = [],
  detailPanel,
  defaultPageSize = 5,
  headerClass,
  pagination = true,
  variant = "primary",
}) => {
  const classes = useStyles();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultPageSize);
  const [order, setOrder] = useState("default");
  const [orderBy, setOrderBy] = useState("");
  const [originalData, setOriginalData] = useState([]);

  useEffect(() => {
    setOriginalData(data);
  }, [data]);

  const visibleRows = useMemo(() => {
    const column = columns.find((col) => col.field === orderBy);
    const comparator =
      column && column.sortComparator
        ? column.sortComparator
        : getComparator(order, orderBy);
    const sortedData =
      order === "default"
        ? originalData
        : tableSort(data, comparator(order, orderBy));
    return (
      sortedData &&
      sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    );
  }, [order, data, orderBy, page, rowsPerPage, originalData, columns]);

  const handleRequestSort = (event, property) => {
    if (orderBy === property) {
      if (order === "asc") {
        setOrder("desc");
      } else if (order === "desc") {
        setOrder("default");
        setOrderBy("");
      } else {
        setOrder("asc");
        setOrderBy(property);
      }
    } else {
      setOrder("asc");
      setOrderBy(property);
    }
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(event.target.value);
    setPage(0);
  };

  const Row = ({ row }) => {
    const [open, setOpen] = useState(true);
    return (
      <>
        <TableRow
          onClick={() => detailPanel && setOpen(!open)}
          style={{ cursor: detailPanel ? "pointer" : "unset" }}
          key={row.code}
        >
          {columns.map((column, index) => {
            const value = column.render
              ? column.render(row)
              : row[column.field];
            return (
              <TableCell
                key={`${column.field}-row-data`}
                align={index === 0 ? "left" : column?.align || "center"}
                sx={{ fontWeight: 500 }}
              >
                {detailPanel && index === 0 && (
                  <IconButton aria-label="expand row" size="small">
                    {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                  </IconButton>
                )}
                {column?.type === "email" ? <EmailRow email={value} /> : value}
              </TableCell>
            );
          })}
        </TableRow>
        {detailPanel && (
          <TableRow>
            <TableCell
              align="center"
              style={{ paddingBottom: 0, paddingTop: 0 }}
              colSpan={6}
            >
              <Collapse in={open} timeout="auto" unmountOnExit>
                <Box margin={1}>
                  <div>{detailPanel(row)}</div>
                </Box>
              </Collapse>
            </TableCell>
          </TableRow>
        )}
      </>
    );
  };

  return (
    <Paper
      sx={{
        borderRadius: "12px",
        width: "100%",
        overflow: "hidden",
        boxShadow: "0px 1px 2px 0px rgba(0,0,0,0.12)",
      }}
    >
      <TableContainer>
        <Table stickyHeader aria-label="sticky table">
          <TableHead
            key="table-header"
            className={headerClass || classes.headerCell}
          >
            <TableRow>
              {columns.map((column, index) => {
                return (
                  <TableCell
                    align={column?.headerAlign || "left"}
                    key={`${column.field}-data`}
                    sortDirection={orderBy === column.field ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === column.field}
                      direction={orderBy === column.field ? order : "asc"}
                      onClick={(event) =>
                        handleRequestSort(event, column.field)
                      }
                    >
                      {column.label}
                      {orderBy === column.field ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === "desc"
                            ? "sorted descending"
                            : order === "asc"
                            ? "sorted ascending"
                            : "default"}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody
            className={variant === "primary" && classes.tableBody}
            sx={{ overflowX: "auto" }}
          >
            {visibleRows.length > 0 ? (
              visibleRows.map((row) => {
                return <Row row={row} />;
              })
            ) : (
              <TableRow>
                <TableCell align="center" colSpan={columns.length + 1}>
                  <Typography variant="body1" align="center">
                    No data available
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {data.length > 0 && pagination && (
        <CustomTablePagination
          count={data.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(val) => setPage(val)}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
        />
      )}
    </Paper>
  );
};

export default CustomTable;
